<script setup lang="ts">
import {computed} from 'vue'
import type {CardsDashboardMemory} from '@shared/types'
import ReviewStack from './ReviewStack.vue'

const props = defineProps<{memory: CardsDashboardMemory; totalCards: number; learnedCards: number}>()

const DECAY_WINDOW = 30

const R = 46
const CIRC = 2 * Math.PI * R

const strengthPercent = computed(() => Math.round(props.memory.avgRetrievability * 100))
const dashOffset = computed(() => CIRC * (1 - props.memory.avgRetrievability))

const bucketSegments = computed(() => [
  {key: 'strong', count: props.memory.buckets.strong, class: 'fill-emerald-500'},
  {key: 'steady', count: props.memory.buckets.steady, class: 'fill-amber-500'},
  {key: 'fragile', count: props.memory.buckets.fragile, class: 'fill-blue-500'},
])

const decayPath = computed(() => {
  const points = props.memory.decayForecast
  if (points.length < 2) return null
  const w = 260
  const h = 64
  const maxV = Math.max(...points.map((p) => p.value), 0.01)
  const step = w / (points.length - 1)
  const toXY = (p: {value: number}, i: number) => `${(i * step).toFixed(1)},${(h - (p.value / maxV) * h).toFixed(1)}`
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toXY(p, i)}`).join(' ')
  const base = `L${w},${h} L0,${h} Z`
  return {line, area: `M0,${h} ${line.replace('M', 'L')} ${base}`, h}
})

const decayDropPercent = computed(() => {
  const points = props.memory.decayForecast
  if (points.length < 2) return 0
  return Math.round((1 - points[points.length - 1].value / Math.max(points[0].value, 0.001)) * 100)
})

const halfLifeText = computed(() =>
  props.memory.halfLifeDays == null
    ? null
    : props.memory.halfLifeDays <= DECAY_WINDOW
      ? props.memory.halfLifeDays
      : null
)

const reviewChips = computed(() => props.memory.nextReviews.filter((b) => b.count > 0))
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-wrap items-center gap-5">
      <div class="relative shrink-0">
        <svg width="116" height="116" viewBox="0 0 116 116" role="img">
          <circle cx="58" cy="58" :r="R" fill="none" stroke="var(--color-muted)" stroke-width="11" />
          <circle
            cx="58"
            cy="58"
            :r="R"
            fill="none"
            stroke="var(--color-chart-2)"
            stroke-width="11"
            stroke-linecap="round"
            :stroke-dasharray="CIRC"
            :stroke-dashoffset="dashOffset"
            transform="rotate(-90 58 58)" />
        </svg>
        <div class="absolute inset-0 flex flex-col items-center justify-center">
          <span class="text-xl font-bold tabular-nums">{{ strengthPercent }}%</span>
          <span class="text-[10px] uppercase tracking-wide text-muted-foreground">{{
            $t('home.dashboard.strength')
          }}</span>
        </div>
      </div>

      <div class="min-w-40 flex-1">
        <div class="text-sm font-medium">{{ $t('home.dashboard.noStudyScenario') }}</div>
        <svg v-if="decayPath" viewBox="0 0 260 64" class="mt-2 block h-16 w-full" preserveAspectRatio="none">
          <path :d="decayPath.area" fill="var(--color-chart-3)" fill-opacity="0.12" />
          <path :d="decayPath.line" fill="none" stroke="var(--color-chart-3)" stroke-width="1.8" />
        </svg>
        <div class="mt-1 text-xs text-muted-foreground">
          <template v-if="halfLifeText !== null">{{ $t('home.dashboard.halfLifeIn', {n: halfLifeText}) }} · </template>
          {{ $t('home.dashboard.decayDrop', {n: decayDropPercent}) }}
        </div>
      </div>
    </div>

    <div>
      <div class="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
        <span>{{ $t('home.dashboard.cardMix') }}</span>
        <span v-if="memory.lapsesCards > 0">{{ $t('home.dashboard.lapsed', {n: memory.lapsesCards}) }}</span>
      </div>
      <ReviewStack :reviews="memory.nextReviews" :total="totalCards" :completed="learnedCards" />
      <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span
          v-for="seg in bucketSegments"
          :key="`l-${seg.key}`"
          class="flex items-center gap-1.5 text-muted-foreground">
          <span class="inline-block size-2 rounded-full" :class="seg.class" />
          {{ $t(`home.dashboard.bucket.${seg.key}`) }} · <b class="font-semibold text-foreground">{{ seg.count }}</b>
        </span>
      </div>
    </div>

    <div v-if="reviewChips.length">
      <div class="mb-1.5 text-xs text-muted-foreground">{{ $t('home.dashboard.nextReviews') }}</div>
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="chip in reviewChips"
          :key="chip.key"
          class="rounded-full border px-2.5 py-0.5 text-xs tabular-nums"
          :class="{
            'border-amber-400/50 bg-amber-400/15 text-amber-300': chip.key === 'overdue',
            'border-violet-500/50 bg-violet-500/15 text-violet-300': chip.key === 'today',
            'text-muted-foreground': !['overdue', 'today'].includes(chip.key),
          }">
          {{ $t(`home.dashboard.review.${chip.key}`) }} · {{ chip.count }}
        </span>
      </div>
    </div>
  </div>
</template>
